/*
 * QOVA Quiz Loader
 *
 * The only file referenced from the storefront. Resolves the current widget
 * bundle via manifest.json and injects it. The manifest is cached short so
 * deploys propagate quickly; the widget bundle itself is content-hashed and
 * cached immutably.
 *
 * Embed (one-time, never changes):
 *   <script>window.__QOVA_QUIZ_BASE__ = "<asset base url>";</script>
 *   <script async src=".../qova-quiz-loader.js"></script>
 *   <div data-qova-quiz="meno"></div>
 *
 * Architecture mirrors Mars Men's quiz loader (read from their live files
 * 2026-08-04). Deliberate divergences are marked QOVA.
 */
(function () {
  "use strict";

  if (window.__qovaQuizLoader) return;
  window.__qovaQuizLoader = true;

  /* QOVA: base is injected by Liquid because the bundle currently lives on the
     Shopify CDN. Flipping to a dedicated origin (quiz.toniiq.com) is a change
     to this one value and nothing else. */
  var BASE = (window.__QOVA_QUIZ_BASE__ || "").replace(/\/$/, "");
  var MANIFEST_URL = window.__QOVA_QUIZ_MANIFEST__ || BASE + "/qova-quiz-manifest.json";
  var FALLBACK_WIDGET = window.__QOVA_QUIZ_FALLBACK__ || BASE + "/qova-quiz-widget.js";
  var loadStarted = false;
  var chromeHidden = false;

  function hasMounts() {
    return document.querySelector("[data-qova-quiz], qova-quiz") !== null;
  }

  /* Hide theme chrome so the quiz owns the viewport.
     QOVA: Mars Men use display:none on the header/footer groups. We do NOT.
     Removing the header from layout is exactly what silently disabled the
     Rebuy gift-with-purchase on the first persona build, so the cart and
     header stack stay in the DOM and are clipped instead. */
  function hideThemeChrome() {
    if (chromeHidden || !hasMounts()) return;
    chromeHidden = true;
    var CLIP =
      "position:absolute!important;width:1px!important;height:1px!important;" +
      "overflow:hidden!important;clip:rect(0 0 0 0)!important;clip-path:inset(50%)!important;" +
      "pointer-events:none!important;margin:0!important;padding:0!important;border:0!important";
    var css =
      /* Theme chrome — clipped, never display:none. Removing the header from
         layout is what silently disabled the Rebuy gift on the first persona
         build; the cart and header stack must stay in the DOM. */
      ".qova-quiz-page #shopify-section-announcement-bar-flow," +
      ".qova-quiz-page .announcement_bar_flow," +
      ".qova-quiz-page .header-wrapper," +
      ".qova-quiz-page .shopify-section-group-header-group," +
      ".qova-quiz-page .shopify-section-group-footer-group," +
      ".qova-quiz-page #shopify-section-footer{" + CLIP + "}" +
      /* QOVA: the site-wide email capture must not fire over a quiz. Mars Men
         deliberately run no email gate; ours would inherit one from the theme.
         Belt and braces — also exclude this URL in Klaviyo's own targeting. */
      ".qova-quiz-page div[class*='kl-private-reset-css']," +
      ".qova-quiz-page .klaviyo-form," +
      ".qova-quiz-page #rebuy-bubble-global-alert-modal," +
      ".qova-quiz-page .yotpo-widget-instance," +
      /* Okendo's floating Reviews tab. Matched by its real classes — the earlier
         id-based selector never matched anything. */
      ".qova-quiz-page .okeReviews," +
      ".qova-quiz-page .oke-reviewsTab," +
      ".qova-quiz-page [id*='okendo-reviews-tab']," +
      /* The floating "Reviews" tab is injected as a fixed, src-less iframe.
         Scoped to the quiz page only, so nothing else on the store is affected. */
      ".qova-quiz-page body > iframe:not([src])," +
      ".qova-quiz-page body > iframe[src='']{display:none!important}" +
      ".qova-quiz-page main,.qova-quiz-page main .page-width,.qova-quiz-page .section_holder{" +
      "max-width:none!important;padding:0!important;margin:0!important}" +
      ".qova-quiz-page,.qova-quiz-page body{background:#FFFFFF!important}" +
      /* Qiu 4 Aug, "can't scroll at the end of the quiz". Klaviyo adds
         .klaviyo-prevent-body-scrolling (body{overflow:hidden}) when its popup
         opens. We clip the popup, but the class stays — so the form is invisible
         AND the page is locked. Higher specificity than Klaviyo's own rule. */
      "html.qova-quiz-page body.klaviyo-prevent-body-scrolling," +
      "html.qova-quiz-page body{overflow:visible!important;position:static!important}";
    /* Qiu/Gautam 4 Aug: the site-wide "International Shipping is Now Available"
       modal was firing over the quiz. It has no stable class, so match it by its
       heading text and remove the dialog it sits in. Quiz page only. */
    (function killShippingModal() {
      /* Qiu/Gautam 4 Aug: the "International Shipping is Now Available" modal
         fires over the quiz. REWRITTEN 5 Aug: the first version read textContent
         of EVERY div on the page 41 times — quadratic work that froze the page
         outright once the DOM got heavier. Only dialog-shaped containers are
         inspected now: a handful of nodes per sweep instead of thousands. */
      function sweep() {
        var boxes = document.querySelectorAll(
          "[role='dialog'],[class*='modal'],[class*='popup'],[id*='modal'],[id*='popup']");
        for (var i = 0; i < boxes.length; i++) {
          var box = boxes[i];
          if (!box.parentNode || box === document.body) continue;
          var t = box.textContent || "";
          if (t.length < 4000 && /international shipping is now available/i.test(t)) {
            box.remove();
            document.body.style.overflow = "";
          }
        }
      }
      sweep();
      document.addEventListener("DOMContentLoaded", sweep);
      var t = 0, iv = setInterval(function () { sweep(); if (++t > 40) clearInterval(iv); }, 400);
    })();

    var style = document.createElement("style");
    style.setAttribute("data-qova-quiz-chrome", "1");
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
    document.documentElement.classList.add("qova-quiz-page");
  }

  function injectWidget(url) {
    if (loadStarted) return;
    loadStarted = true;
    var s = document.createElement("script");
    s.src = url;
    s.async = true;
    s.setAttribute("data-qova-quiz", "1");
    s.onerror = function () {
      /* Last-resort fallback: the manifest pointed at a hashed bundle that no
         longer exists (mid-deploy race). Try the unhashed alias. */
      if (s.dataset.fallbackTried) return;
      s.dataset.fallbackTried = "1";
      var fb = document.createElement("script");
      fb.src = FALLBACK_WIDGET;
      fb.async = true;
      document.head.appendChild(fb);
    };
    document.head.appendChild(s);
  }

  function resolve(widget) {
    /* Manifest values may be a bare filename or an absolute URL. */
    if (/^https?:\/\//.test(widget) || widget.indexOf("//") === 0) return widget;
    return BASE + "/" + widget.replace(/^\//, "");
  }

  function loadFromManifest() {
    if (!hasMounts()) return;
    hideThemeChrome();

    /* Mars Men get fast deploy propagation from short-cache + SWR headers on
       their own origin. Shopify serves theme assets with a long immutable
       cache, so the manifest must be revalidated explicitly or a deploy can
       take hours to reach a returning visitor. The manifest is ~80 bytes. */
    /* no-store only bypasses the BROWSER cache. Shopify's CDN serves theme
       assets with max-age=31557600 (one year), so the manifest URL baked into
       cached page HTML can stay pinned to an old bundle — which is exactly how
       the frozen 48ee9a29 build kept resurrecting on 2026-08-05. A per-minute
       cache-buster makes every manifest fetch reach origin within 60s. */
    var cb = (MANIFEST_URL.indexOf("?") === -1 ? "?" : "&") + "cb=" + Math.floor(Date.now() / 60000);
    fetch(MANIFEST_URL + cb, { credentials: "omit", cache: "no-store" })
      .then(function (r) {
        if (!r.ok) throw new Error("manifest fetch failed: " + r.status);
        return r.json();
      })
      .then(function (m) {
        var w = m && typeof m.widget === "string" ? m.widget : null;
        injectWidget(w ? resolve(w) : FALLBACK_WIDGET);
      })
      .catch(function () {
        injectWidget(FALLBACK_WIDGET);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", loadFromManifest, { once: true });
  } else {
    loadFromManifest();
  }
})();
